import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import DeptSwitcher, { ccDepartments } from '../components/ContactCenter/DeptSwitcher';
import { useAuth } from '../context/AuthContext';
import LiveRoster, { fmtSince } from '../components/ContactCenter/LiveRoster';
import { fmtAsOf, fmtClock, fmtCount, fmtPct, STATUS, statusColor, statusTint } from '../components/ContactCenter/format';
import { btn, c, card, tbl } from '../theme';
import { CC_PAGE_MAX_WIDTH } from '../components/ContactCenter/pageWidth';

// Contact Center · Live Ops — the floor now: what is waiting, who is free,
// each pod's day so far, today's service level by the hour, and the roster.
// The live worker refreshes the data every 30 seconds (today's totals every
// minute); the page asks every 15. When the data is older than two minutes the
// page says so in red rather than show a floor that only looks quiet.

const CHANNELS = ['Voice', 'VIP', 'Callback', 'Email', 'Chat', 'Voicemail'];
const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

function LiveTile({ channel, t }) {
  const busy = t.waiting > 0;
  return (
    <section style={{ ...s.tile, boxShadow: busy ? `inset 3px 0 0 ${c.dangerText}` : 'none' }} aria-label={`${channel} waiting`}>
      <div style={s.tTitle}>{channel}</div>
      <div style={{ ...s.tValue, color: busy ? c.dangerText : c.textStrong }}>{fmtCount(t.waiting)}</div>
      <div style={s.tSub}>waiting</div>
      <div style={s.tSub}>Longest {t.longestWaitSecs == null ? '—' : fmtSince(t.longestWaitSecs)}</div>
    </section>
  );
}

function PodCard({ p }) {
  const [channel, setChannel] = useState('Voice');
  const f = p.today[channel];
  const live = p.live[channel];
  const cell = (label, value, tone) => (
    <div style={s.cell}><div style={s.cLabel}>{label}</div><div style={{ ...s.cValue, color: statusColor(tone) }}>{value}</div></div>
  );
  return (
    <section style={s.pod} aria-label={`${p.pod} today`}>
      <div style={s.podHead}>
        <strong>{p.pod}</strong>
        {/* As the old card: the queue only while something waits, and its
            oldest only when that is known. */}
        <span style={s.tSub}>
          {fmtCount(f?.answered ?? 0)} {channel.toLowerCase()} answered
          {live.waiting > 0 && <> · {fmtCount(live.waiting)} in queue
            {live.longestWaitSecs > 0 && <> · {fmtClock(live.longestWaitSecs)} oldest</>}</>}
        </span>
      </div>
      <div style={s.chTabs} role="tablist" aria-label={`${p.pod} channel`}>
        {CHANNELS.map((ch) => (
          <button key={ch} type="button" role="tab" aria-selected={channel === ch} onClick={() => setChannel(ch)}
            style={{ ...(channel === ch ? s.chOn : s.ch), ...(p.live[ch].waiting > 0 ? { color: c.dangerText } : {}) }}>
            {ch}{p.live[ch].waiting > 0 ? ' •' : ''}
          </button>
        ))}
      </div>
      <div style={s.cells}>
        {cell('Waiting', fmtCount(live.waiting), live.waiting > 0 ? 'bad' : null)}
        {channel !== 'Callback' && cell('Service level', fmtPct(f?.serviceLevel ?? null), STATUS.serviceLevel(f?.serviceLevel ?? null))}
        {cell('Speed of answer', fmtClock(f?.asa ?? null))}
        {cell(channel === 'Callback' ? 'AHT (connect)' : 'AHT', fmtClock(f?.aht ?? null), STATUS.aht(f?.aht ?? null))}
        {cell('Offered', fmtCount(f?.offered ?? 0))}
        {cell('Answered', fmtCount(f?.answered ?? 0))}
        {cell('Abandons', fmtCount(f?.abandons ?? 0))}
        {p.adherence && cell(`Adherence now${p.adherence.n ? ` (${p.adherence.n})` : ''}`,
          p.adherence.value == null ? '—' : `${p.adherence.value}%`, STATUS.adherence(p.adherence.value))}
        {cell('Occupancy today', fmtPct(p.occupancy ?? null))}
      </div>
    </section>
  );
}

// Service level by the hour, pods down and hours across: a grid rather than
// nine lines, which no palette keeps apart.
function HourlyGrid({ pods }) {
  const hours = [...new Set(pods.flatMap((p) => p.hourlySl.map((h) => h.hour)))].sort((a, b) => a - b);
  if (!hours.length) return null;
  const label = (h) => `${((h + 11) % 12) + 1}${h < 12 ? 'a' : 'p'}`;
  return (
    <section style={s.panel} aria-label="Service level by hour">
      <h2 style={s.h2}>Service level by hour today · Voice + VIP</h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead><tr><th style={s.th}>Pod</th>{hours.map((h) => <th key={h} style={s.thNum}>{label(h)}</th>)}</tr></thead>
          <tbody>
            {pods.map((p) => (
              <tr key={p.pod}>
                <td style={{ ...s.td, fontWeight: 550 }}>{p.pod}</td>
                {hours.map((h) => {
                  const v = p.hourlySl.find((x) => x.hour === h)?.serviceLevel ?? null;
                  const tone = STATUS.serviceLevel(v);
                  return <td key={h} style={{ ...s.num, background: tone ? statusTint(tone) : undefined, color: statusColor(tone) }}>{v == null ? '' : Math.round(v)}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={s.foot}>Percent of calls answered within 60 seconds. Green from 95, amber from 90. Blank: no calls that hour.</p>
    </section>
  );
}

export default function ContactCenterLive() {
  const { user } = useAuth();
  const [dept, setDept] = useState(() => ccDepartments(user, 'live_ops')[0]?.id || 'support');
  const [pods, setPods] = useState([]);                  // empty = every pod
  const q = useQuery({
    queryKey: ['cc-live', dept, pods.join(',')],
    queryFn: () => api.get('/contact-center/live', { params: { dept, ...(pods.length ? { pods: pods.join(',') } : {}) } }),
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });
  const d = q.data;

  return (
    <div style={s.page}>
      <style>{css}</style>
      <PageHeader
        eyebrow="Contact Center"
        title={d?.deptLabel && d.deptLabel !== 'Support' ? `Live Ops · ${d.deptLabel}` : 'Live Ops'}
        subtitle={d ? `${pods.length ? pods.join(', ') : 'All pods'} · the floor now` : 'The floor now'}
        meta={d?.observedAt ? <span style={{ ...s.chip, color: d.stale ? c.dangerText : c.textMuted }}>Updated {fmtAsOf(d.observedAt)}</span> : null}
        actions={<DeptSwitcher view="live_ops" value={dept} onChange={(id) => { setDept(id); setPods([]); }} />}
        alignActions="end"
      />
      <div style={s.row} role="group" aria-label="Pods">
        <span style={s.label}>Pods</span>
        <button type="button" aria-pressed={!pods.length} onClick={() => setPods([])} style={!pods.length ? s.pillOn : s.pill}>All</button>
        {(d?.allPods || []).map((p) => (
          <button key={p} type="button" aria-pressed={pods.includes(p)} onClick={() => setPods((cur) => toggle(cur, p))}
            style={pods.includes(p) ? s.pillOn : s.pill}>{p}</button>
        ))}
      </div>

      {q.isLoading && <LoadingSpinner />}
      {q.error && <div style={s.error}>Could not load Live Ops: {q.error.message}</div>}
      {d?.stale && (
        <div style={s.stale} role="alert">
          {d.observedAt
            ? `The live data stopped updating at ${fmtAsOf(d.observedAt)}. What is shown may no longer be true.`
            : 'The live worker has not reported yet. Nothing below is live.'}
        </div>
      )}

      {d && (
        <>
          <div className="cc-live-tiles">
            {CHANNELS.map((ch) => <LiveTile key={ch} channel={ch} t={d.live[ch]} />)}
            {d.adherence && (
              <section style={s.tile} aria-label="Adherence now">
                <div style={s.tTitle}>Adherence now</div>
                <div style={{ ...s.tValue, color: statusColor(STATUS.adherence(d.adherence.value)) }}>
                  {d.adherence.value == null ? '—' : `${d.adherence.value}%`}
                </div>
                <div style={{ ...s.tSub, color: d.adherence.stale ? c.dangerText : s.tSub.color }}>
                  {d.adherence.stale ? 'Schedule data is not current' : `of ${fmtCount(d.adherence.n)} scheduled agents`}
                </div>
              </section>
            )}
            <section style={s.tile} aria-label="Agents available">
              <div style={s.tTitle}>Agents available</div>
              <div style={s.tValue}>{fmtCount(d.agentsAvailable.length)}</div>
              {d.agentsAvailable.length > 0 && (
                <details style={s.tSub}><summary>Who</summary>
                  {d.agentsAvailable.map((a) => <div key={a.userId}>{a.name || 'Unknown agent'} · {a.pod}</div>)}
                </details>
              )}
            </section>
          </div>
          <h2 style={s.h2Page}>Today so far</h2>
          <div style={s.pods}>{d.pods.map((p) => <PodCard key={p.pod} p={p} />)}</div>
          <HourlyGrid pods={d.pods} />
          <LiveRoster roster={d.roster} thresholds={d.thresholds} />
        </>
      )}
    </div>
  );
}

const s = {
  page:   { maxWidth: CC_PAGE_MAX_WIDTH, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '14px' },
  row:    { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  label:  { fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.textMuted, minWidth: '48px' },
  pill:   { ...btn.secondaryXs },
  pillOn: { ...btn.secondaryXs, background: c.navy, color: '#fff', borderColor: c.navy },
  chip:   { fontSize: '11px' },
  error:  { fontSize: '13px', color: c.dangerText },
  stale:  { fontSize: '13px', fontWeight: 600, color: c.dangerText, background: c.dangerTint, padding: '10px 14px', borderRadius: '8px' },
  tile:   { ...card.base, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '2px' },
  tTitle: { fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.textMuted },
  tValue: { fontSize: '28px', fontWeight: 650, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' },
  tSub:   { fontSize: '11px', color: c.textMuted },
  h2Page: { fontSize: '13px', fontWeight: 650, color: c.text, margin: '6px 0 0' },
  pods:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' },
  pod:    { ...card.base, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' },
  podHead:{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', fontSize: '13px', color: c.text },
  chTabs: { display: 'flex', gap: '2px', flexWrap: 'wrap' },
  ch:     { all: 'unset', cursor: 'pointer', fontSize: '11px', padding: '3px 7px', borderRadius: '6px', color: c.textMuted },
  chOn:   { all: 'unset', cursor: 'pointer', fontSize: '11px', padding: '3px 7px', borderRadius: '6px', color: c.textStrong, background: c.surfaceSunk, fontWeight: 600 },
  cells:  { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '6px' },
  cell:   { background: c.surfaceSunk, borderRadius: '6px', padding: '6px 8px' },
  cLabel: { fontSize: '10px', color: c.textMuted },
  cValue: { fontSize: '15px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  panel:  { ...card.base, padding: '14px 0 10px' },
  h2:     { fontSize: '13px', fontWeight: 650, color: c.text, margin: '0 18px 8px' },
  table:  { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:     { ...tbl.thSm, textAlign: 'left' },
  thNum:  { ...tbl.thRSm, textAlign: 'center' },
  td:     { ...tbl.tdSm, color: c.textStrong, whiteSpace: 'nowrap' },
  num:    { ...tbl.tdRSm, textAlign: 'center', color: c.textStrong },
  foot:   { fontSize: '11px', color: c.textMuted, margin: '8px 18px 0' },
};

// The headline tiles, as a stylesheet rather than an inline object, because the
// fix is a media query and an inline object cannot express one.
//
// Fixed at four columns, not `auto-fit, minmax(140px, 1fr)`. The tiles are
// small enough that auto-fit fitted six or seven of them across a wide screen
// and laid the whole set out in one long strip, which is what the CS team
// asked to be four-across in two rows.
//
// There are seven or eight tiles, not always eight: six channels and Agents
// available are unconditional, but Adherence now is rendered only when the
// live worker has reported adherence. So this is 4 + 4 normally and 4 + 3 when
// it has not — the last slot is simply empty, which is why the count does not
// need to divide by four.
const css = `
.cc-live-tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}
@media (max-width: 900px) {
  .cc-live-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 560px) {
  .cc-live-tiles { grid-template-columns: minmax(0, 1fr); }
}
`;
